import type { ReactNode } from 'react';
import type {
  CalendarEvent,
  CalendarGroup,
  CalendarKindMap,
} from '../../types.js';
import { useCalendarMessages } from '../../i18n.js';
import { EventCard } from '../EventCard.js';
import { eventInstanceKey } from '../../utils/identity.js';
import type { RelatedEventMatch } from './single-form-helpers.js';

export interface SingleFormSidebarProps {
  readonly previewEvent: CalendarEvent;
  readonly kindTheme: CalendarKindMap;
  readonly groups?: readonly CalendarGroup[] | undefined;
  readonly relatedMatches: readonly RelatedEventMatch[];
  readonly onSelectRelatedEvent?: ((event: CalendarEvent) => void) | undefined;
}

export function SingleFormSidebar(props: SingleFormSidebarProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    previewEvent,
    kindTheme,
    groups,
    relatedMatches,
    onSelectRelatedEvent,
  } = props;
  const exact = relatedMatches.some((match) => match.exact);

  return (
    <aside className="vq-cal__composer-preview" aria-live="polite">
      <header className="vq-cal__composer-preview-head">
        <span>{messages.composerSectionPreview}</span>
      </header>
      <EventCard event={previewEvent} kindTheme={kindTheme} groups={groups} />
      {relatedMatches.length > 0 && (
        <section className="vq-cal__composer-related">
          <div className="vq-cal__composer-related-head">
            <span>
              {exact
                ? messages.composerRelatedEventsExactTitle
                : messages.composerRelatedEventsTitle}
            </span>
          </div>
          {exact && (
            <p className="vq-cal__composer-related-hint">
              {messages.composerRelatedEventsHint}
            </p>
          )}
          <div className="vq-cal__composer-related-list">
            {relatedMatches.map((match) => (
              <EventCard
                key={eventInstanceKey(match.event)}
                event={match.event}
                kindTheme={kindTheme}
                groups={groups}
                onClick={onSelectRelatedEvent}
                density="compact"
              />
            ))}
          </div>
        </section>
      )}
    </aside>
  );
}
